import React from 'react';
import { reduxForm } from 'redux-form';
import BaseForm from '../form/base-form';
import FormField from '../form/form-field';
import DateField from '../form/date-field';
import GenderField from '../form/gender-field';
import DistanceSelectField from '../stage/distance-select';
import LocalizedStrings from '../../localization/strings';
import Button from '../button';
import { validateDate, validateEmail, validateRequiredFields } from '../../utils/form';
import moment from 'moment';
import { isEnglish } from '../../localization/language-cookie';
import Alert from '../alert';
import Select from '../form/select';
import { getCountries } from '../../utils/countries';
import ShirtField from '../form/shirt-field';

const fields = [
  'id',
  'first_name',
  'last_name',
  'birth_date',
  'phone',
  'friend_phone',
  'city',
  'club',
  'email',
  'gender',
  'stageId',
  'distanceId',
  'createdAt',
  'payed',
  'payment',
  'uuid',
  'agree_on_regulations',
  'medical_confirmation',
  'participate_in_insurance',
  'marketing_email',
  'country',
  'gdpr_confirmation',
  'promo_code',
  'related_participant_identifier',
  'extra_info',

  'billing_is_company',
  'billing_name',
  'billing_email',
  'billing_company_code',
  'billing_company_vat',
  'billing_company_address',


  'team_participants[].first_name',
  'team_participants[].last_name',
  'team_participants[].gender',
  'team_name',
  'shirt_size',

  'custom_field'
];

function validate(values, formInstance) {

  let errors = {
    _error: null
  };
  errors = validateRequiredFields(values, [
    'first_name',
    'last_name',
    'birth_date',
    'phone',
    'email',
    'distanceId',
    'stageId',
    'gender',
    'agree_on_regulations',
    'medical_confirmation',
    'gdpr_confirmation'
  ], errors);

  if (window.CARE_CONFIG.invoicesEnabled && !values.id) {

    errors = validateRequiredFields(values, [
      'billing_name',
      'billing_email'
    ], errors);
    errors = validateEmail(values, 'billing_email', errors);
  }
  if (window.CARE_CONFIG.customParticipantField && window.CARE_CONFIG.customParticipantField.required) {
    errors = validateRequiredFields(values, [
      'custom_field'
    ], errors);
  }



  const selectedStage = formInstance.stages.find(stage => stage.id === formInstance.form.stageId.value);
  const selectedDistance = selectedStage ? selectedStage.distances.find(distance => distance.id === formInstance.form.distanceId.value) : null;

  if (selectedStage && selectedStage.display_extra_info_field && selectedStage.extra_info_field_required) {
    errors = validateRequiredFields(values, [
      'extra_info'
    ], errors);

  }


  if (selectedDistance && selectedDistance.is_team_distance) {
    if (!values.team_name) {
      errors.team_name = LocalizedStrings.requiredField;
    }
    if (values.team_participants.length < selectedDistance.min_team_members) {
      errors.team_name = LocalizedStrings.minTeamMembers + ' ' + selectedDistance.min_team_members;
    }
    if (values.team_participants) {
      const teamParticipantsErrors = [];

      values.team_participants.forEach((teamParticipant, index) => {
        const participantErrors = {};
        if (!teamParticipant.first_name) {
          participantErrors['first_name'] = LocalizedStrings.requiredField;
        }
        if (!teamParticipant.last_name) {
          participantErrors['last_name'] = LocalizedStrings.requiredField;
        }
        if (!teamParticipant.gender) {
          participantErrors['gender'] = LocalizedStrings.requiredField;
        }
        teamParticipantsErrors[index] = participantErrors;

      });
      if (teamParticipantsErrors.length > 0) {
        errors.team_participants = teamParticipantsErrors;
      }
    }
  }

  errors = validateEmail(values, 'email', errors);
  errors = validateDate(values, 'birth_date', errors);

  return errors;
}

class ParticipantForm extends BaseForm {
  constructor(props) {
    super(props);
    this.state = {
      price: null,
      calculating: false
    };
  }


  showAlert() {
    const {
      showAlert = true
    } = this.props;

    return showAlert;
  }

  calculatePrice() {
    let { stageId, distanceId, birth_date, createdAt, promo_code } = this.props.values;

    if (birth_date) {
      birth_date = moment(birth_date).format('YYYY-MM-DD');
    }
    if (createdAt) {
      createdAt = moment(createdAt).format('YYYY-MM-DD');
    }

    if (distanceId && birth_date && stageId) {
      this.setState({ calculating: true });
      fetch(`/api/participant/calculate-price?distanceId=${distanceId ? distanceId : ''}&stageId=${stageId ? stageId : ''}&birth_date=${birth_date ? birth_date : ''}&created_at=${createdAt ? createdAt : ''}&promo=${promo_code ? promo_code : ''}`).then(response => {
        if (response.status >= 200 && response.status < 300) {
          return response.json();
        } else {
          return Promise.reject(response);
        }

      }).then((response) => {
        this.setState({ price: response.price, calculating: false });
      }).catch(resp => {
        resp.json().then((response) => {
          if (response.errors) {
            alert(response.errors);
          }
        });
      });
    }
  }

  getInsuranceLabel() {
    return isEnglish()
      ? window.CARE_CONFIG.settings.insurance_text_in_registration_en
      : window.CARE_CONFIG.settings.insurance_text_in_registration;
  }

  componentDidUpdate(prevProps) {
    let { stageId, distanceId, birth_date, promo_code } = this.props.values;
    if (prevProps.values.stageId !== stageId || prevProps.values.distanceId !== distanceId || prevProps.values.birth_date !== birth_date || prevProps.values.promo_code !== promo_code) {
      this.calculatePrice();
    }
  }

  renderCustomField(field) {
    let fieldUI;
    const fieldConfig = window.CARE_CONFIG.customParticipantField;
    if (fieldConfig.type === 'select') {
      fieldUI = <Select clearable={false} options={fieldConfig.values} noResultsText={''} placeholder={''} {...field}
                        onChange={(item) => field.onChange(item.value)} />;
    }
    if (fieldConfig.type === 'text') {
      fieldUI = <input ref="name" placeholder={fieldConfig.label} type="text" {...field} />;
    }

    return (<div className="form-row-new">
      <FormField label={fieldConfig.label} name="gender" field={field}>
        {fieldUI}
      </FormField>

    </div>);
  }

  updateInvoiceProperty(changedProperty) {
    const {
      first_name,
      last_name,
      email,
      billing_name,
      billing_email
    } = this.props.fields;
    if(['first_name', 'last_name'].includes(changedProperty)){
      billing_name.onChange(`${first_name.value} ${last_name.value}`);
    }
    if(changedProperty==='email'){
      billing_email.onChange(email.value);
    }

  }

  render() {
    const {
      stages,
      submitting,
      error,
      handleSubmit,
      disableStageSelect = false,
      displayPayed = false,
      fields: {
        first_name,
        last_name,
        birth_date,
        phone,
        friend_phone,
        city,
        club,
        email,
        gender,
        stageId,
        distanceId,
        payed,
        agree_on_regulations,
        medical_confirmation,
        participate_in_insurance,
        marketing_email,
        country,
        gdpr_confirmation,
        promo_code,
        related_participant_identifier,

        billing_is_company,
        billing_name,
        billing_email,
        billing_company_code,
        billing_company_vat,
        billing_company_address,

        team_participants,
        team_name,

        extra_info,
        custom_field,
        shirt_size
      }
    } = this.props;

    const { id, payment } = this.props.values;
    const { price } = this.state;
    const selectedStage = stages.find(stage => stage.id === stageId.value);
    const isBillingDisabled = id !== null && id !== undefined;
    const selectedDistance = selectedStage ? selectedStage.distances.find(distance => distance.id === distanceId.value) : null;

    return (
      <div>
        <form onSubmit={handleSubmit(this.submit.bind(this))}>
          {error && <Alert type="error">{error}</Alert>}
          {related_participant_identifier.value &&
            <div>{LocalizedStrings.seasonPersonIdentifier}: {related_participant_identifier.value}</div>}

          <div className="distance-price-row">
            <DistanceSelectField stageField={stageId} distanceField={distanceId}
                                 disableStageSelect={disableStageSelect} />

            <div className="price-display">
              <span className="price-title">{LocalizedStrings.price}:</span>
              <span className="amount-display">{price !== null ? price : (payment ? payment.price : 0)}</span>
            </div>
          </div>
          <fieldset>
            <legend>{LocalizedStrings.participantLegendTitle}</legend>
            <div className="form-row-new">
              <FormField label={LocalizedStrings.firstName} name="name" field={first_name}>
                <input ref="name" placeholder={LocalizedStrings.firstName} type="text" {...first_name} onBlur={() => {
                  first_name.onBlur();
                  if(!isBillingDisabled) {
                    this.updateInvoiceProperty('first_name');
                  }
                }} />
              </FormField>

              <FormField label={LocalizedStrings.lastName} name="last_name" field={last_name}>
                <input ref="last_name" placeholder={LocalizedStrings.lastName} type="text" {...last_name}
                       onBlur={() => {
                         last_name.onBlur();
                         if(!isBillingDisabled) {
                           this.updateInvoiceProperty('last_name');
                         }
                       }}
                />
              </FormField>
              <FormField label={LocalizedStrings.gender} name="gender" field={gender}>
                <GenderField field={gender}></GenderField>
              </FormField>


              {selectedDistance && selectedDistance.enable_shirt_selection ?
                  <FormField label={LocalizedStrings.shirtSize} name="shirt_size" field={shirt_size}>
                    <ShirtField field={shirt_size}/>
                  </FormField>

                : null}
            </div>
            <div className="form-row-new">

              <FormField label={LocalizedStrings.birthDate} name="birth_date" field={birth_date}>
                <DateField {...birth_date} timeFormat={false} noTimeZoneDate={true}
                           viewMode="years"></DateField>
              </FormField>
              <FormField label={LocalizedStrings.email} name="email" field={email}>
                <input ref="email" placeholder={LocalizedStrings.email} type="text" {...email}
                       onBlur={() => {
                         last_name.onBlur();
                         if(!isBillingDisabled) {
                           this.updateInvoiceProperty('email');
                         }
                       }}
                />
              </FormField>
              <FormField label={LocalizedStrings.phone} name="phone" field={phone}>
                <input ref="phone" placeholder={LocalizedStrings.phone} type="text" {...phone} />
              </FormField>
            </div>

            <div className="form-row-new">
              <FormField label={LocalizedStrings.friendPhone} name="friend_phone" field={friend_phone}>
                <input ref="friend_phone" placeholder={LocalizedStrings.friendPhone} type="text" {...friend_phone} />
              </FormField>
              <FormField label={LocalizedStrings.city} name="city" field={city}>
                <input ref="city" placeholder={LocalizedStrings.city} type="text" {...city} />
              </FormField>
              <FormField label={LocalizedStrings.club} name="club" field={club}>
                <input ref="club" placeholder={LocalizedStrings.club} type="text" {...club} />
              </FormField>

              <FormField label={LocalizedStrings.country} name="country" field={country}>

                {window.CARE_CONFIG.countrySelectionEnabled ?
                  <Select {...country} clearable={false} options={getCountries()}
                          placeholder={LocalizedStrings.country} onChange={(item)=>country.onChange(item.value)} /> :
                  <input ref="country" placeholder={LocalizedStrings.country} type="text" {...country} />
                }


              </FormField>
            </div>

            {window.CARE_CONFIG.customParticipantField && this.renderCustomField(custom_field)}
          </fieldset>
          {selectedDistance && selectedDistance.is_team_distance && <fieldset>
            <legend>{LocalizedStrings.teamInfo}</legend>
            <div className="form-row-new">
              <FormField label={LocalizedStrings.teamName} name="team_name" field={team_name}>
                <input ref="team_name" placeholder={LocalizedStrings.teamName} type="text" {...team_name} />
              </FormField>
            </div>
            <legend>{LocalizedStrings.teamParticipants}</legend>
            {team_participants.map((teamParticipants, index) => <div className="form-row-new" key={index}>
              <FormField label={LocalizedStrings.firstName} name="name" field={teamParticipants.first_name}>
                <input ref="name" placeholder={LocalizedStrings.firstName}
                       type="text" {...teamParticipants.first_name} />
              </FormField>

              <FormField label={LocalizedStrings.lastName} name="last_name" field={teamParticipants.last_name}>
                <input ref="last_name" placeholder={LocalizedStrings.lastName}
                       type="text" {...teamParticipants.last_name} />
              </FormField>
              <FormField label={LocalizedStrings.gender} name="gender" field={teamParticipants.gender}>
                <GenderField field={teamParticipants.gender}></GenderField>
              </FormField>
              <div className="button-col">
                <a className="btn btn-red" onClick={() => {
                  team_participants.removeField(index);
                }}>
                  {LocalizedStrings.remove}
                </a>
              </div>

            </div>)}

            <a className="btn m-top-10 m-bot-10" onClick={() => {
              team_participants.addField();
            }}><i /> {LocalizedStrings.addTeamMember}
            </a>

          </fieldset>}
          {(window.CARE_CONFIG.invoicesEnabled && !isBillingDisabled) &&
            <fieldset>
              <legend>{LocalizedStrings.paymentInfoLegend}</legend>

              <FormField label={LocalizedStrings.billingIsCompony} name="billing_is_company" field={billing_is_company}
                         className="checkbox-field"
                         dots={false}>
                <input type="checkbox" {...billing_is_company} disabled={isBillingDisabled} />
              </FormField>

              <div className="form-row-new">
                <FormField label={LocalizedStrings.billingName} name="billing_name" field={billing_name}>
                  <input ref="billing_name" placeholder={LocalizedStrings.billingName} type="text" {...billing_name}
                         disabled={isBillingDisabled} />
                </FormField>

                <FormField label={LocalizedStrings.billingEmail} name="billing_email" field={billing_email}>
                  <input ref="billing_email" placeholder={LocalizedStrings.billingEmail} type="text" {...billing_email}
                         disabled={isBillingDisabled} />
                </FormField>
              </div>
              {billing_is_company.value && <div className="form-row-new">
                <FormField label={LocalizedStrings.billingCompanyCode} name="billing_company_code"
                           field={billing_company_code}>
                  <input ref="billing_company_code" placeholder={LocalizedStrings.billingCompanyCode}
                         type="text" {...billing_company_code} disabled={isBillingDisabled} />
                </FormField>
                <FormField label={LocalizedStrings.billingVatCode} name="billing_company_vat"
                           field={billing_company_vat}>
                  <input ref="billing_company_vat" placeholder={LocalizedStrings.billingVatCode}
                         type="text" {...billing_company_vat} disabled={isBillingDisabled} />
                </FormField>
                <FormField label={LocalizedStrings.billingAddress} name="billing_company_address"
                           field={billing_company_address}>
                  <input ref="billing_company_address" placeholder={LocalizedStrings.billingAddress}
                         type="text" {...billing_company_address} disabled={isBillingDisabled} />
                </FormField>
              </div>}

            </fieldset>
          }

          {
            window.CARE_CONFIG.discountEnabled && (
              <div className="form-row-new">
                <FormField label={LocalizedStrings.promoCode} name="club" field={promo_code}>
                  <input ref="promo_code" placeholder={LocalizedStrings.promoCode} type="text" {...promo_code}
                         disabled={id != null} />
                </FormField>
              </div>
            )
          }


          {selectedStage && selectedStage.display_extra_info_field ?
            <div className="form-row-new">
              <FormField label={selectedStage.extra_info_field_title} name="extra_info" field={extra_info}>
                <input ref="title" placeholder={selectedStage.extra_info_field_title} type="text" {...extra_info} />

              </FormField>
            </div>

            : null}

          <div className="form-row-new force-col">
            <FormField label={LocalizedStrings.gdprConfirmation} name="gdpr_confirmation"
                       field={gdpr_confirmation} className="checkbox-field" dots={false} link={'/gdpr'}>
              <input type="checkbox" {...gdpr_confirmation} />
            </FormField>


            <FormField label={LocalizedStrings.agreeOnTerms} name="agree_on_regulations"
                       field={agree_on_regulations} className="checkbox-field"
                       link={`/stages/${stageId.value}/regulations`} dots={false}>
              <input type="checkbox" {...agree_on_regulations} />
            </FormField>

            <FormField label={LocalizedStrings.medicalConfirmation} name="medical_confirmation"
                       field={medical_confirmation} className="checkbox-field" dots={false}>
              <input type="checkbox" {...medical_confirmation} />
            </FormField>

            {selectedStage && selectedStage.insurance_enabled
              ? <FormField label={this.getInsuranceLabel()} name="participate_in_insurance"
                           field={participate_in_insurance} className="checkbox-field" link={'/insurance'}
                           dots={false}>
                <input type="checkbox" {...participate_in_insurance} />
              </FormField>
              : null}


            <FormField label={LocalizedStrings.marketingEmailConfirmation} name="marketing_email"
                       field={marketing_email} className="checkbox-field" dots={false}>
              <input type="checkbox" {...marketing_email} />
            </FormField>
          </div>


          {displayPayed
            ?
            <FormField label={LocalizedStrings.payed} name="payed" field={payed} className="checkbox-field"
                       dots={false}>
              <input type="checkbox" {...payed} />
            </FormField>
            : null}

          <div className="actions-row">

            {this.state.calculating ? <div>{LocalizedStrings.calculating}</div> :
              <Button loading={submitting} submit={true} disabled={submitting} type="main-color">
                {LocalizedStrings.submit}
              </Button>}

          </div>

        </form>
      </div>
    );
  }
}

export default reduxForm({
  form: 'participant-form',
  fields,
  validate,
  destroyOnUnmount: true
}, state => ({
  initialValues: state.participant.participant,
  stages: state.stage.availableStages.items
}))(ParticipantForm);



// WEBPACK FOOTER //
// ./src/js/components/participant/participant-form.js